// OrderCard.jsx
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import ReactLoading from 'react-loading';
import { HiOutlineDocumentText, HiOutlineCheckCircle } from 'react-icons/hi2';
import { IoChatbubbles, IoChatbubblesOutline } from 'react-icons/io5';

import api from '../../../../hooks/api';
import { OrderStepper } from './OrderStepper';
// import { ConfirmationModal } from '../modals/ConfirmationModal'; // در صورت عدم نیاز در جاهای دیگر میتوانید پاک کنید
import { OrderChatPanel } from './OrderChatPanel';
import BuyerInvoicePreviewModal from '../../../Print/BuyerInvoice/components/BuyerInvoicePreviewModal';
import './OrderCard.css';
import { SurveyDeliveryModal } from './SurveyDeliveryModal';

const toPersianDigits = (num) => {
    if (num === null || num === undefined) return '';
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return num.toString().replace(/\d/g, (x) => persianDigits[x]);
};

export const OrderCard = ({ data, onRemoveOrder, ChatId, activeTab, isCancelled }) => {
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
    const [showReportPanel, setShowReportPanel] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [showSurveyModal, setShowSurveyModal] = useState(false); // <--- مودال جدید نظرسنجی
    const [isDeliveryLoading, setIsDeliveryLoading] = useState(false);

    // ۱. تشخیص دقیق وضعیت ابطال
    const isCancelledTab = Boolean(
        activeTab === 'cancelled' ||
        isCancelled === true ||
        data.activeTab === 'cancelled' ||
        data.status === 'cancelled' ||
        data.status_id === 7 ||
        data.currentStep === 7 ||
        data.isRepealed ||
        data.is_repeal ||
        data.isRepeal ||
        data.status_name === 'ابطال' ||
        data.status_name === 'ابطال شده'
    );

    // ۲. تشخیص قطعی تحویل فاکتور
    const isDeliveredTab = Boolean(
        activeTab === 'delivered' ||
        data.isDelivered === true ||
        data.isDelivered === 1 ||
        data.isDeliveredToCustomer === true ||
        data.isDeliveredToCustomer === 1 ||
        data.status === 'delivered' ||
        data.status_name === 'تحویل شده'
    );

    const currentStatusNumber = Number(data.status_id ?? data.currentStep ?? 2);
    const targetStatusId = isCancelledTab ? 7 : currentStatusNumber;
    
    // وضعیت نهایی شدن فاکتور
    const isFinalizedStep = currentStatusNumber === 6;

    const hasChat = Boolean(
        (data.reporters && typeof data.reporters === 'object' && Object.keys(data.reporters).length > 0) ||
        (Array.isArray(data.reporter) && data.reporter.length > 0) ||
        (Array.isArray(data.reports) && data.reports.length > 0) ||
        data.reporter_header_id ||
        data.has_chat ||
        data.chat_count > 0
    );

    const handleFetchAndShowInvoice = async () => {
        // [کدهای فچ کردن فاکتور بدون تغییر باقی ماند...]
        try {
            Swal.fire({
                title: '<h3 style="font-size: 1.25rem; font-weight: 700; margin: 0; color: #1f2937;">در حال پردازش فاکتور</h3>',
                html: '<p style="font-size: 0.95rem; color: #64748b; margin-top: 10px;">لطفاً کمی صبر کنید...</p>',
                allowOutsideClick: false,
                showConfirmButton: false,
                width: '85%',
                maxWidth: '360px',
                padding: '1.5rem',
                customClass: { popup: 'font-sans' },
                didOpen: () => {
                    const popup = Swal.getPopup();
                    if (popup) popup.style.borderRadius = '20px';
                    Swal.showLoading();
                }
            });

            const response = await api.post('invoice/search/byFactorId', {
                invoice_id: data.id,
                business_id: data.business_id
            });

            if (response.data && response.data.success) {
                const fetchedInvoiceData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                const trackingCode = fetchedInvoiceData.trackingCodePost || data.trackingCodePost;
                const buyerFullName = `${fetchedInvoiceData.name_buyer_archive || ''} ${fetchedInvoiceData.family_buyer_archive || ''}`.trim() || fetchedInvoiceData.buyer?.name || 'نامشخص';

                const sendCostPrice = Number(fetchedInvoiceData.send_cost_price ?? data.send_cost_price ?? 0);
                let totalConsumer = 0, totalProfit = 0, totalVat = 0, totalPackCost = 0;
                const rawDetails = fetchedInvoiceData.invoice_details || fetchedInvoiceData.items || [];

                const formattedItems = rawDetails.map(detail => {
                    const count = Number(detail.amount || detail.count || 0);
                    const basePrice = Number(detail.price || detail.originalPrice || 0);
                    const discount = Number(detail.discount_price || 0);
                    const vat = Number(detail.vat_price || 0);
                    const totalPrice = Number(detail.totalPrice || detail.total || (basePrice * count));
                    const packCost = Number(detail.pack_cost || 0);

                    totalConsumer += totalPrice;
                    totalProfit += discount;
                    totalVat += vat;
                    totalPackCost += packCost;

                    return {
                        id: detail.id,
                        commodityName: detail.commodityName || detail.name || 'نامشخص',
                        name: detail.commodityName || detail.name || 'نامشخص',
                        count,
                        amount: count,
                        originalPrice: basePrice,
                        price: basePrice,
                        discount_price: discount,
                        vat_price: vat,
                        pack_cost: packCost,
                        ourPrice: count > 0 ? (basePrice - (discount / count)) : basePrice,
                        total: totalPrice - discount,
                        totalPrice
                    };
                });

                const totalShippingAndPack = sendCostPrice + totalPackCost;
                const calculatedFinalPayment = (totalConsumer - totalProfit) + totalVat + totalShippingAndPack;
                const finalAmount = Number(fetchedInvoiceData.total_price_invoice_archive || fetchedInvoiceData.price || calculatedFinalPayment);

                const payload = {
                    ...data,
                    ...fetchedInvoiceData,
                    send_cost_price: sendCostPrice,
                    factorNumber: fetchedInvoiceData.factorNumber || data.factorNumber || data.id,
                    price: finalAmount,
                    date: fetchedInvoiceData.created_at || data.date,
                    status: isCancelledTab ? 'cancelled' : (isDeliveredTab ? 'delivered' : data.status),
                    currentStep: targetStatusId,
                    trackingCodePost: trackingCode,
                    finance_trnsactions: fetchedInvoiceData.finance_trnsactions || data.finance_trnsactions || [],
                    seller: {
                        name: fetchedInvoiceData.business?.title || fetchedInvoiceData.shop || data.shop || 'نامشخص',
                        phone: '',
                        address: ''
                    },
                    buyer: {
                        name: buyerFullName,
                        family: '',
                        phone: fetchedInvoiceData.mobile_buyer_archive || fetchedInvoiceData.buyer?.mobile || '',
                        address: fetchedInvoiceData.address_buyer_archive || fetchedInvoiceData.buyer?.address || data.address || '',
                        postCode: fetchedInvoiceData.post_code_buyer_archive || fetchedInvoiceData.buyer?.post_code || ''
                    },
                    invoice_details: formattedItems,
                    items: formattedItems,
                    totals: {
                        consumer: totalConsumer,
                        profit: totalProfit,
                        vat: totalVat,
                        shipping: totalShippingAndPack,
                        finalPayment: finalAmount
                    },
                    summary: {
                        sendCost: sendCostPrice,
                        totalPackCost,
                        totalConsumer,
                        totalProfit,
                        totalVat,
                        totalShippingAndPack,
                        finalPayment: finalAmount
                    }
                };

                Swal.close();
                setSelectedInvoiceData(payload);
                setIsInvoiceModalOpen(true);
            } else {
                throw new Error("دیتای فاکتور یافت نشد.");
            }
        } catch (error) {
            console.error("Error fetching invoice:", error);
            Swal.fire({
                icon: 'error',
                title: '<h3 style="font-size: 1.25rem; font-weight: 700; margin: 0;">خطا!</h3>',
                text: 'مشکلی در دریافت اطلاعات فاکتور پیش آمد. لطفا مجدداً تلاش کنید.',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'بستن',
                width: '85%',
                maxWidth: '360px',
                padding: '1.5rem',
                customClass: { popup: 'font-sans' }
            });
        }
    };

    // تابع جدید برای مدیریت دریافت آرایه نظرسنجی و ارسال به سمت سرور
    const handleConfirmDelivery = async (surveyRatings) => {
        setIsDeliveryLoading(true);
        try {
            await api.post(`invoice/delivery/store`, { 
                invoice_id: data.id,
                ratings: surveyRatings // آرایه دریافت شده از مدال در اینجا به API ارسال می‌شود
            });
            setShowSurveyModal(false);
            if (onRemoveOrder) onRemoveOrder(data.id);
        } catch {
            Swal.fire({ icon: 'error', title: 'خطا', text: "خطا در ثبت اطلاعات." });
            setIsDeliveryLoading(false);
        }
    };

    const handleCopyTrackingCode = () => {
        if (!data.trackingCodePost) return;
        navigator.clipboard.writeText(data.trackingCodePost).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    };

    return (
        <div className="order-card-wrapper">
            <div className={`order-card ${isCancelledTab ? 'cancelled-mode' : ''}`}>
                <div className="card-header">
                    <div className="card-header-right">
                        <span>شماره: <span className="label">{toPersianDigits(data.factorNumber)}</span></span>
                        <span>فروشگاه: <span className="label">{data.shop || data.business?.title}</span></span>
                    </div>
                    <div className="card-header-left">
                        <span>تاریخ: {toPersianDigits(data.date)}</span>
                        <span>مبلغ: {toPersianDigits(data.price || data.total_price_invoice_archive)} ت</span>
                    </div>
                </div>

                <OrderStepper
                    currentStep={currentStatusNumber}
                    statusId={data.status_id}
                    isError={isCancelledTab}
                    isCancelled={isCancelledTab}
                    cancelledStatusId={currentStatusNumber}
                    isDelivered={isDeliveredTab}
                />

                {(currentStatusNumber >= 5) && data.trackingCodePost && (
                    <div className="tracking-code-section">
                        <div className="tracking-code-info">
                            <span className="tracking-label">کد رهگیری پستی:</span>
                            <span className="tracking-value">{toPersianDigits(data.trackingCodePost)}</span>
                        </div>
                        <button className={`tracking-btn ${isCopied ? 'copied' : ''}`} onClick={handleCopyTrackingCode}>
                            {isCopied ? 'کپی شد' : 'کپی کد'}
                        </button>
                    </div>
                )}

                <div className="card-actions">
                    {/* تغییر کلیک دکمه به باز کردن مدال نظرسنجی */}
                    {!isCancelledTab && !isDeliveredTab && isFinalizedStep && (
                        <button
                            type="button"
                            className="action-btn confirm-btn"
                            onClick={() => setShowSurveyModal(true)} 
                            disabled={isDeliveryLoading}
                            title="تایید تحویل مرسوله"
                        >
                            <span>تحویل گرفتم</span>
                        </button>
                    )}

                    <button type="button" className="action-btn detail-btn" onClick={handleFetchAndShowInvoice} title="مشاهده فاکتور">
                        <HiOutlineDocumentText size={18} />
                        <span>مشاهده فاکتور</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowReportPanel(!showReportPanel)}
                        title={showReportPanel ? "بستن گفتگو" : "گفتگو با فروشگاه"}
                        className={`action-btn invoice-chat-toggle-btn ${hasChat ? 'has-active-chat' : ''} ${showReportPanel ? 'drawer-opened' : ''}`}
                    >
                        {hasChat || showReportPanel ? <IoChatbubbles size={20} /> : <IoChatbubblesOutline size={20} />}
                    </button>
                </div>
            </div>

            {showReportPanel && (
                <OrderChatPanel
                    orderId={data.id}
                    targetStatusId={targetStatusId}
                    isCancelled={isCancelledTab}
                    isDelivered={isDeliveredTab}
                    isFinalized={isDeliveredTab}
                />
            )}

            {/* فراخوانی کامپوننت مستقل مدال نظرسنجی */}
            <SurveyDeliveryModal
                isOpen={showSurveyModal}
                onClose={() => setShowSurveyModal(false)}
                onSubmit={handleConfirmDelivery}
                isSubmitting={isDeliveryLoading}
            />

            <BuyerInvoicePreviewModal
                isOpen={isInvoiceModalOpen}
                onClose={() => setIsInvoiceModalOpen(false)}
                invoiceData={selectedInvoiceData}
            />
        </div>
    );
};
